/**
 * Green Flag — text styles
 *
 * Named typography presets, referenced by Text/Heading via `textStyle="hero"` etc.
 * Each maps to font family + size + weight + line height + letter spacing per
 * the Figma audit (`README.md` §Visual Foundations / Type).
 */

import { defineTextStyles } from "@chakra-ui/react";

export const textStyles = defineTextStyles({
  // Display — League Gothic wordmark
  logo: {
    description: "The GREEN FLAG wordmark. League Gothic, condensed, all caps.",
    value: {
      fontFamily: "display",
      fontWeight: "normal",
      fontSize: "3xl",        // 24px
      lineHeight: "base",
      letterSpacing: "wide",
      textTransform: "uppercase",
    },
  },
  logoMobile: {
    value: {
      fontFamily: "display",
      fontWeight: "normal",
      fontSize: "xl",         // 16px
      lineHeight: "base",
      letterSpacing: "wide",
      textTransform: "uppercase",
    },
  },

  // Headlines — Figma Header 1/2/3
  heroDesktop: {
    description: "Figma Header 1 — Medium 36. Hero / onboarding / marketing banners.",
    value: { fontFamily: "body", fontWeight: "medium", fontSize: "4xl", lineHeight: "tight" },
  },
  hero: {
    description: "Figma Header 2 — Medium 24. Page section titles ('Steven, 37').",
    value: { fontFamily: "body", fontWeight: "medium", fontSize: "3xl", lineHeight: "tight" },
  },
  pageHeading: {
    description: "hero, one step up on desktop — Medium 24, 28 from md. The h1 of an admin, host or partner page ('New event', 'Check-In').",
    value: { fontFamily: "body", fontWeight: "medium", fontSize: { base: "3xl", md: "28px" }, lineHeight: "tight" },
  },
  pageTitle: {
    description: "Figma Header 3 — Regular 16. App page titles, profile callouts.",
    value: { fontFamily: "body", fontWeight: "normal", fontSize: "xl", lineHeight: "tight" },
  },

  // UI
  eyebrow: {
    description: "Kit-level UPPER treatment of LABEL — Regular 10, 0.04em, uppercase.",
    value: {
      fontFamily: "body",
      fontWeight: "normal",
      fontSize: "sm",         // 10px
      lineHeight: "tight",
      letterSpacing: "wider",
      textTransform: "uppercase",
    },
  },
  body: {
    description: "Kit helper — Regular 14 (not in Figma).",
    value: { fontFamily: "body", fontWeight: "normal", fontSize: "lg", lineHeight: "tight" },
  },
  bodyLight: {
    description: "Figma Paragraph 2 — Light 14. Card details ('Saturday, April 14th').",
    value: { fontFamily: "body", fontWeight: "light", fontSize: "lg", lineHeight: "tight" },
  },
  bodySecondary: {
    description: "Kit helper — Regular 13. Supporting copy that sits under a 14px body line (benefit blurbs, inline errors, the header's organization name).",
    value: { fontFamily: "body", fontWeight: "normal", fontSize: "13px", lineHeight: "tight" },
  },
  bodySecondaryStrong: {
    description: "Semibold 13. The emphasized figure on a bodySecondary line (a spots count).",
    value: { fontFamily: "body", fontWeight: "semibold", fontSize: "13px", lineHeight: "tight" },
  },
  code: {
    description: "Kit helper — Mono 14. A path, id or other literal in body copy ('/partner/the-bar').",
    value: { fontFamily: "mono", fontWeight: "normal", fontSize: "lg", lineHeight: "tight" },
  },
  finePrint: {
    description: "Kit helper — Regular 12. Footnotes and asides ('No account yet? Ask your contact to invite you').",
    value: { fontFamily: "body", fontWeight: "normal", fontSize: "md", lineHeight: "tight" },
  },
  sectionHeading: {
    description: "Semibold 16. The heading on a card or empty state — one step under `hero`, above `label`.",
    value: { fontFamily: "body", fontWeight: "semibold", fontSize: "xl", lineHeight: "tight" },
  },
  statValue: {
    description: "Medium 28. The number on a stat tile where the League Gothic `displayTitle` would shout — the partner portal.",
    value: { fontFamily: "body", fontWeight: "medium", fontSize: "28px", lineHeight: "tight" },
  },
  label: {
    description: "Figma Paragraph 1 — Medium 14. Card titles, link underlines.",
    value: { fontFamily: "body", fontWeight: "medium", fontSize: "lg", lineHeight: "tight" },
  },
  labelStrong: {
    description: "Semibold 14. A label that heads its own group: a discount's title, the button that adds one.",
    value: { fontFamily: "body", fontWeight: "semibold", fontSize: "lg", lineHeight: "tight" },
  },
  cardTitle: {
    description: "Alias of label — Figma Paragraph 1 (Medium 14).",
    value: { fontFamily: "body", fontWeight: "medium", fontSize: "lg", lineHeight: "tight" },
  },
  caption: {
    description: "Figma Caption — Semibold 12. Coloured tags & callouts ('3 Sparks Attending').",
    value: { fontFamily: "body", fontWeight: "semibold", fontSize: "md", lineHeight: "tight" },
  },
  buttonLabel: {
    description: "Figma Button — Semibold 12. CTAs & active tabs ('Sign Up').",
    value: { fontFamily: "body", fontWeight: "semibold", fontSize: "md", lineHeight: "tight" },
  },
  buttonLabelInactive: {
    description: "Deselected tab — Regular 12.",
    value: { fontFamily: "body", fontWeight: "normal", fontSize: "md", lineHeight: "tight" },
  },
  inputLabel: {
    description: "Figma LABEL — Regular 10, no caps, no tracking. Input titles, small UI identifiers, internal back-button.",
    value: { fontFamily: "body", fontWeight: "normal", fontSize: "sm", lineHeight: "tight" },
  },
  cardInput: {
    description: "Card entry inputs — Medium 15, tight tracking. The Stripe iframes read it back as computed CSS, so it has to be a named style rather than props on one input.",
    value: { fontFamily: "body", fontWeight: "medium", fontSize: "15px", letterSpacing: "-0.005em" },
  },
  chipLabel: {
    description: "Sparks chip — micro bold 8.",
    value: { fontFamily: "body", fontWeight: "bold", fontSize: "2xs", lineHeight: "tight" },
  },
  navLabel: {
    value: { fontFamily: "body", fontWeight: "normal", fontSize: "sm", lineHeight: "tight" },
  },
  badgeNum: {
    value: { fontFamily: "body", fontWeight: "bold", fontSize: "xs", lineHeight: "tight" },
  },
  eyebrowBold: {
    description:
      "Bold 11, 0.14em tracking, uppercase. Card/dialog section headers (Settings cards, consent dialog). Matches the inline pattern predating this style.",
    value: {
      fontFamily: "body",
      fontWeight: "bold",
      fontSize: "11px",
      lineHeight: "tight",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
    },
  },
  legalFine: {
    description:
      "Regular 11, 1.5 line height. Legal fine print (the text-notification consent disclosure).",
    value: {
      fontFamily: "body",
      fontWeight: "normal",
      fontSize: "11px",
      lineHeight: "1.5",
    },
  },
  switchLabel: {
    description:
      "Semibold 10, 0.06em tracking, uppercase. Channel-toggle labels (Settings → Notification).",
    value: {
      fontFamily: "body",
      fontWeight: "semibold",
      fontSize: "sm",       // 10px
      lineHeight: "tight",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
    },
  },

  // Compat aliases for pre-migration callers (lib/chakraTheme.ts). Safe to
  // remove once each consumer is audited and moved to the new names above.
  welcomeHeading: {
    value: {
      fontFamily: "body",
      fontSize: { base: "24px", md: "36px" },
      fontWeight: "medium",
      lineHeight: "1.3",
    },
  },
  displayTitle: {
    value: {
      fontFamily: "display",
      fontSize: "4rem",
      fontWeight: "normal",
      lineHeight: "1",
      textTransform: "uppercase",
    },
  },
  headingLg: {
    value: { fontFamily: "display", fontSize: "2rem", fontWeight: "normal" },
  },
  displayHeading: {
    description: "League Gothic 32, uppercase, set solid. A shouted line inside a sheet (the check-in code's event title, 'You're checked in!').",
    value: {
      fontFamily: "display",
      fontSize: "2rem",
      fontWeight: "normal",
      lineHeight: "1",
      textTransform: "uppercase",
    },
  },
  headingMd: {
    value: { fontFamily: "display", fontSize: "1.5rem", fontWeight: "normal" },
  },
  headingSm: {
    value: { fontFamily: "display", fontSize: "1.25rem", fontWeight: "normal" },
  },
  headingBold: {
    value: { fontFamily: "display", fontSize: "1.5rem", fontWeight: "bold" },
  },
  sectionTitle: {
    value: { fontFamily: "body", fontSize: "16px", fontWeight: "bold" },
  },
  bodyLg: {
    value: { fontFamily: "body", fontSize: "1.2rem", fontWeight: "normal" },
  },
  bodyMuted: {
    value: { fontFamily: "body", fontSize: "1rem", fontWeight: "normal", color: "fg.muted" },
  },
  textLink: {
    value: { fontFamily: "body", fontSize: "12px", fontWeight: "semibold" },
  },
  documentBold: {
    description:
      "Bold emphasis inside document body copy — clause labels and table headers in the agreement preview. Pairs with bodyLight.",
    value: { fontFamily: "body", fontWeight: "bold", fontSize: "lg", lineHeight: "tight" },
  },
});
